import React from 'react';
import { Phone, Truck, Clock, MessageCircle, Zap, ShieldCheck } from 'lucide-react';

const WHATSAPP_NUMBER = '573193650538';
const WHATSAPP_MESSAGE = 'Hola, quiero hacer un pedido';

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.567-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.50 3.488"/>
  </svg>
);

export const WhatsAppPromoSection: React.FC = () => {
  const handleClick = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative overflow-hidden py-20 px-4 sm:px-6 lg:px-8">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#075E54] via-[#128C7E] to-[#25D366]" />

      {/* Floating bubbles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-[10%] w-20 h-20 bg-white/10 rounded-full animate-bounce" style={{ animationDuration: '3s' }} />
        <div className="absolute top-1/4 right-[15%] w-14 h-14 bg-white/10 rounded-full animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }} />
        <div className="absolute bottom-20 left-[20%] w-10 h-10 bg-white/10 rounded-full animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />
        <div className="absolute bottom-1/3 right-[25%] w-16 h-16 bg-white/10 rounded-full animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '1.5s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold mb-6 animate-pulse">
              <Zap className="w-4 h-4 text-yellow-300" />
              ¡El método más rápido!
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
              ¿Prefieres pedir por{' '}
              <span className="text-emerald-200">WhatsApp</span>?
            </h2>

            <p className="text-lg text-emerald-100 mb-8 max-w-lg mx-auto lg:mx-0">
              Escríbenos directamente y un farmacéutico te atenderá al instante. 
              Sin registros, sin complicaciones.
            </p>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Respuesta inmediata</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Truck className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Domicilio express</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">100% Seguro</span>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleClick}
              className="group relative inline-flex items-center gap-3 px-8 py-5 bg-white text-[#128C7E] rounded-2xl font-bold text-lg shadow-2xl hover:shadow-white/30 hover:scale-105 active:scale-95 transition-all duration-300 overflow-hidden"
            >
              {/* Shine effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-emerald-100/50 to-transparent" />
              <WhatsAppIcon className="w-7 h-7 relative z-10" />
              <span className="relative z-10">Chatear en WhatsApp</span>
              <Phone className="w-5 h-5 relative z-10 group-hover:rotate-12 transition-transform" />
            </button>

            <p className="mt-4 text-sm text-emerald-200/80">
              (+57) 319 365 0538 • Lunes a Domingo 8AM - 10PM
            </p>
          </div>

          {/* Right - Animated Phone Visual */}
          <div className="relative flex items-center justify-center">
            {/* Outer rings */}
            <div className="absolute w-72 h-72 rounded-full border-2 border-white/20 animate-ping" style={{ animationDuration: '3s' }} />
            <div className="absolute w-56 h-56 rounded-full border border-white/30 animate-ping" style={{ animationDuration: '3s', animationDelay: '0.5s' }} />

            {/* Main phone mockup */}
            <div className="relative w-72 bg-white rounded-[2.5rem] shadow-2xl p-4 transform hover:scale-105 transition-transform duration-500">
              {/* Phone notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-900 rounded-b-2xl" />

              {/* Screen content */}
              <div className="bg-gradient-to-b from-gray-50 to-white rounded-[2rem] p-5 pt-8 min-h-[320px] flex flex-col">
                {/* Chat header */}
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center">
                    <WhatsAppIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">ConfiFarma Amiga</p>
                    <p className="text-xs text-emerald-500 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                      En línea
                    </p>
                  </div>
                </div>

                {/* Chat bubbles */}
                <div className="flex-1 space-y-3 py-4">
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[85%]">
                      <p className="text-sm text-gray-700">¡Hola! 👋 Bienvenido a ConfiFarma Amiga</p>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[85%]">
                      <p className="text-sm text-gray-700">¿En qué puedo ayudarte hoy?</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-[#DCF8C6] rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[85%]">
                      <p className="text-sm text-gray-700">Hola, quiero hacer un pedido</p>
                    </div>
                  </div>
                </div>

                {/* Typing indicator */}
                <div className="flex items-center gap-1 text-gray-400 text-xs">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>•</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>•</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>•</span>
                  <span className="ml-1">Escribiendo...</span>
                </div>
              </div>
            </div>

            {/* Floating elements around phone */}
            <div className="absolute -top-4 -right-4 w-14 h-14 bg-white rounded-2xl shadow-lg flex items-center justify-center animate-bounce" style={{ animationDuration: '2s' }}>
              <Truck className="w-7 h-7 text-[#25D366]" />
            </div>
            <div className="absolute -bottom-2 -left-6 w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }}>
              <Clock className="w-6 h-6 text-[#128C7E]" />
            </div>
            <div className="absolute top-1/2 -right-8 w-10 h-10 bg-white rounded-lg shadow-lg flex items-center justify-center animate-bounce" style={{ animationDuration: '3s', animationDelay: '1s' }}>
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
